import type { ToastContent, ToastOptions } from 'vue3-toastify'

type ToastModule = typeof import('vue3-toastify')

let toastModule: Promise<ToastModule> | undefined
let toastStyles: Promise<unknown> | undefined

// The CSS loads on its own: if only the stylesheet fails (e.g. an old hashed
// chunk after a deploy), the toast still shows, just unstyled.
const loadToastStyles = () => {
  toastStyles ??= import('vue3-toastify/dist/index.css').catch((error) => {
    toastStyles = undefined
    console.error('Failed to load the toast styles', error)
  })
}

const loadToast = () => {
  toastModule ??= import('vue3-toastify').catch((error) => {
    toastModule = undefined
    throw error
  })
  return toastModule
}

/**
 * Same call shape as vue3-toastify's `toast`, but the library and its CSS are
 * only downloaded when the first toast is shown, so they stay out of the
 * initial page load (KFT-4323).
 *
 * Only `toast(content, options)` and `toast.POSITION.TOP_CENTER` exist here.
 * For anything else (`toast.error`, other positions, …) extend this helper;
 * importing values from `vue3-toastify` directly is blocked by ESLint so the
 * library doesn't creep back into the startup bundle.
 */
export const toast = Object.assign(
  (content: ToastContent, options?: ToastOptions): void => {
    if (import.meta.server) {
      return
    }
    loadToastStyles()
    loadToast().then(
      ({ toast: showToast }) => {
        try {
          showToast(content, options)
        } catch (error) {
          console.error('Failed to show the toast', error)
        }
      },
      (error) => {
        console.error('Failed to load the toast library', error)
      }
    )
  },
  { POSITION: { TOP_CENTER: 'top-center' } as const }
)
